import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  TextField,
  MenuItem,
  Button,
  FormControlLabel,
  Switch,
  CircularProgress,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { debounce } from 'lodash';
import config from '../config';
import ServerDropdown from './ServerDropdown';
import CharacterTable from './CharacterTable';
import { getFinalServerName } from '../data/servers';
import { getAllRelatedServers } from '../utils/serverUtils';

interface Character {
  _id: string;
  characterName: string;
  characterSurname: string;
  server: string;
  class: string;
  race: string;
  level: number;
  displayedGuildId?: string;
  displayedGuild?: {
    _id: string;
    name: string;
  };
  auth0Id: string;
  ownerName: string | null;
}

interface Guild {
  _id: string;
  name: string;
  server: string;
}

const ITEMS_PER_PAGE = 20;

const CharacterBrowser: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const showMyCharactersParam = searchParams.get('showMyCharacters');
  const { getAccessTokenSilently, user } = useAuth0();
  const [characters, setCharacters] = useState<Character[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedServer, setSelectedServer] = useState<string>('');
  const [includeMergedPlayers, setIncludeMergedPlayers] = useState<boolean>(false);
  const [guildOptions, setGuildOptions] = useState<Guild[]>([]);
  const [selectedGuild, setSelectedGuild] = useState<string>('');
  const [characterSearch, setCharacterSearch] = useState<string>('');
  const [cachedToken, setCachedToken] = useState<string | null>(null);
  const [counts, setCounts] = useState<{ characterCount: number; userCount: number }>({ characterCount: 0, userCount: 0 });
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>('');

  // Function to get token, using cached version if available
  const getToken = useCallback(async () => {
    if (cachedToken) return cachedToken;
    
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: config.auth0.audience,
          scope: 'openid profile email'
        },
        cacheMode: "on"  // Explicitly enable SDK caching
      });
      setCachedToken(token);
      return token;
    } catch (error) {
      console.error('CharacterBrowser: Error getting token:', error);
      throw error;
    }
  }, [getAccessTokenSilently, cachedToken]);

  // Fetch counts
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const token = await getToken();
        const response = await axios.get(`${config.api.baseURL}/api/characters/counts`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setCounts(response.data);
      } catch (error) {
        console.error('CharacterBrowser: Error fetching counts:', error);
      }
    };

    fetchCounts();
  }, [getToken]);

  // Fetch characters with pagination
  const fetchCharacters = useCallback(async (pageNum: number, isNewSearch: boolean = false) => {

    if (isLoading) {
      return;
    }
    
    try {
      setIsLoading(true);
      const token = await getToken();
      
      const params: any = {
        page: pageNum,
        limit: ITEMS_PER_PAGE,
      };

      // Add filters to params if they exist
      if (selectedServer) params.server = selectedServer;
      if (selectedGuild) params.guild = selectedGuild;
      if (characterSearch) params.search = characterSearch;
      if (includeMergedPlayers && selectedServer) {
        const relatedServers = Array.from(getAllRelatedServers(selectedServer));
        params.servers = relatedServers;
      }

      const response = await axios.get<{ characters: Character[]; total: number }>(`${config.api.baseURL}/api/characters`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params
      });


      if (isNewSearch) {
        setCharacters(response.data.characters);
      } else {
        setCharacters(prev => [...prev, ...response.data.characters]);
      }

      const newHasMore = response.data.characters.length === ITEMS_PER_PAGE;
      setHasMore(newHasMore);
      setPage(pageNum);
    } catch (error) {
      console.error('CharacterBrowser: Error fetching characters:', error);
      setError('Failed to fetch characters');
    } finally {
      setIsLoading(false);
    }
  }, [getToken, selectedServer, selectedGuild, characterSearch, includeMergedPlayers, isLoading]);

  // Reset and fetch when filters change
  useEffect(() => {

    const resetAndFetch = async () => {
      setCharacters([]);
      setPage(1);
      setHasMore(true);
      setError(null);
      
      await fetchCharacters(1, true);
    };

    resetAndFetch();
  }, [selectedServer, selectedGuild, characterSearch, includeMergedPlayers]);

  const loadMore = useCallback(() => {
    
    if (!isLoading && hasMore) {
      fetchCharacters(page + 1);
    }
  }, [fetchCharacters, isLoading, hasMore, page, characters.length]);

  // Log component mount/unmount
  useEffect(() => {
    return () => {
      setCachedToken(null); // Clear cached token on unmount
    };
  }, []);

  // Log auth state changes
  useEffect(() => {

  }, [user, cachedToken]);

  // Search guilds when server changes
  useEffect(() => {
    let isSubscribed = true;

    const searchGuilds = async () => {
      if (!selectedServer) {
        setGuildOptions([]);
        setSelectedGuild('');
        return;
      }

      try {
        const token = await getToken();
        const response = await axios.get<Guild[]>(`${config.api.baseURL}/api/guilds/search`, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params: {
            server: selectedServer,
            query: ''  // Empty query to get all guilds for the server
          }
        });
        if (isSubscribed) {
          setGuildOptions(response.data);
        }
      } catch (error) {
        console.error('Error searching guilds:', error);
      }
    };

    searchGuilds();
    return () => {
      isSubscribed = false;
    };
  }, [selectedServer, getToken]);  // Only depend on selectedServer and getToken

  const handleDelete = async (characterId: string) => {
    if (!window.confirm('Are you sure you want to delete this character?')) {
      return;
    }

    try {
      const token = await getToken();
      await axios.delete(`${config.api.baseURL}/api/characters/${characterId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Remove the character from the local state
      setCharacters(prev => prev.filter(c => c._id !== characterId));
    } catch (error) {
      console.error('Error deleting character:', error);
      setError('Failed to delete character');
    }
  };

  const handleServerChange = (value: string) => {
    setSelectedServer(value);
  };

  const handleGuildChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedGuild(event.target.value);
  };

  // Create a debounced function to update the search
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setCharacterSearch(value);
    }, 300),
    []
  );

  const handleCharacterSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value); // Update the input field immediately
    debouncedSearch(value); // Debounce the actual search
  };

  const handleMergedPlayersChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeMergedPlayers(event.target.checked);
  };

  // Filter characters based on selected filters
  const filteredCharacters = characters.filter(character => {
    // Server filter with merged servers support
    if (selectedServer) {
      if (includeMergedPlayers) {
        const connectedServers = getAllRelatedServers(selectedServer);
        if (!connectedServers.has(character.server)) {
          return false;
        }
      } else if (character.server !== selectedServer) {
        return false;
      }
    }

    // Guild filter
    if (selectedGuild && character.displayedGuild?.name !== selectedGuild) {
      return false;
    }

    // Character name search
    if (characterSearch) {
      const searchLower = characterSearch.toLowerCase();
      const fullName = `${character.characterName} ${character.characterSurname}`.toLowerCase();
      if (!fullName.includes(searchLower)) {
        return false;
      }
    }

    return true;
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5">
            Character Browser
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Find old friends!
          </Typography>
        </Box>
        <Typography variant="body2" color="primary" sx={{ mx: 2 }}>
          (Messaging coming soon)
        </Typography>
        <Button 
          variant="contained" 
          color="primary"
          onClick={() => navigate('/add-character')}
        >
          Add Character
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Search Characters"
            value={searchInput}
            onChange={handleCharacterSearchChange}
            placeholder="Enter character name..."
            sx={{ minWidth: 200 }}
          />
          <ServerDropdown
            value={selectedServer}
            onChange={handleServerChange}
            sx={{ minWidth: 200 }}
            showAllServersOption={true}
          />
          <FormControlLabel
            control={
              <Switch
                checked={includeMergedPlayers}
                onChange={handleMergedPlayersChange}
                disabled={!selectedServer}
              />
            }
            label="Include Merged Servers"
          />
          <TextField
            select
            label="Guild"
            value={selectedGuild}
            onChange={handleGuildChange}
            disabled={!selectedServer}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">
              <em>All Guilds</em>
            </MenuItem>
            {guildOptions.map((guild) => (
              <MenuItem key={guild._id} value={guild.name}>
                {guild.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <InfiniteScroll
        dataLength={characters.length}
        next={loadMore}
        hasMore={hasMore}
        loader={
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        }
        endMessage={
          <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
            {characters.length > 0 ? "No more characters to load." : "No characters found."}
          </Typography>
        }
      >
        <CharacterTable 
          characters={characters}
          onDelete={handleDelete}
          showActions={false}
        />
      </InfiniteScroll>
    </Box>
  );
};

export default CharacterBrowser;